import React, { forwardRef, useCallback } from 'react';
import { DayClickEventHandler, DayPicker, useInput } from 'react-day-picker';

import { Global } from '@emotion/react';
import { Rifm } from 'rifm';

import formatDateWithFormat from '../../utility/formatting/formatDateWithFormat';
import getFormattingFunction from '../../utility/formatting/getFormattingFunction';
import { validateDate } from '../../utility/validation';
import FlexRow from '../FlexRow';
import FormInput from '../FormInput';
import Popover from '../Popover';
import { DATE_FORMAT, MAX_INPUT_SYMBOLS, REGEXP_NUMBERS_ONLY } from './constants';
import './styles';
import { globalStyles } from './styles';

const defaultInputValidationRules = {
    fromDate: new Date(),
};

type Props = React.ComponentProps<typeof FormInput> & {
    value: Date;
    disabledDays?: {};
    calendarProps?: {
        toMonth?: Date;
        modifiers?: {};
        fromMonth?: Date;
        selected?: [];
    };
    inputValidationRules?: {};
    datePickerClassName?: string;
    onChange?: (ev: { target: { value: Date } }) => void;
    onSelectDay?: (value: Date) => void;
};

const formatDate = getFormattingFunction(DATE_FORMAT);

const DatePicker = (props: Props, ref) => {
    const {
        className,
        datePickerClassName,
        value,
        label,
        size,
        name,
        disabled,
        disabledDays = { before: new Date() },
        calendarProps = {},
        onChange,
        onSelectDay,
        inputValidationRules = defaultInputValidationRules,
    } = props;

    const { inputProps, dayPickerProps, reset } = useInput({
        defaultSelected: value,
        format: DATE_FORMAT,
        required: true,
        ...inputValidationRules,
    });

    const handleInput = useCallback(
        (value) => {
            inputProps.onChange?.({ target: { value } });
            const validDate = validateDate(value, inputValidationRules);

            if (validDate) {
                onChange?.({ target: { value: validDate } });
            }
        },
        [inputProps, onChange]
    );

    const handleBlur = useCallback(() => {
        const currentDate = formatDateWithFormat(value, 'mm/dd/yyyy');

        if (currentDate !== inputProps.value) {
            reset();
        }
    }, [value, inputProps, reset]);

    const renderInput = useCallback(
        ({ ref, toggleVisibility }) => {
            const renderInputField = ({ value, onChange }) => {
                return (
                    <FormInput
                        name={name}
                        size={size}
                        label={label}
                        {...inputProps}
                        {...{ value, onChange }}
                        disabled={disabled}
                        onBlur={handleBlur}
                    />
                );
            };

            const handleWrapperClick = () => !disabled && toggleVisibility();

            return (
                <div ref={ref} onClick={handleWrapperClick}>
                    <Rifm
                        accept={REGEXP_NUMBERS_ONLY}
                        mask={MAX_INPUT_SYMBOLS <= inputProps.value.length}
                        format={formatDate}
                        value={inputProps.value}
                        onChange={handleInput}
                    >
                        {renderInputField}
                    </Rifm>
                </div>
            );
        },
        [name, label, disabled, size, inputProps, handleInput]
    );

    const renderCalendar = useCallback(
        ({ toggleVisibility }) => {
            const handleDayClick: DayClickEventHandler = (value, modifiers, event) => {
                if (modifiers.disabled) {
                    return;
                }
                toggleVisibility();
                dayPickerProps.onDayClick?.(value, modifiers, event);
                onChange?.({ target: { value } });
                onSelectDay?.(value);
            };

            return (
                <div>
                    <DayPicker
                        {...dayPickerProps}
                        {...calendarProps}
                        {...{
                            disabled: disabledDays,
                            onDayClick: handleDayClick,
                        }}
                    />
                </div>
            );
        },
        [dayPickerProps, onChange, onSelectDay]
    );

    return (
        <>
            <Global styles={globalStyles} />
            <FlexRow className={className} itemsSpacing={15}>
                <Popover
                    disableHideOnInnerClick
                    renderContent={renderCalendar}
                    className={datePickerClassName}
                    ref={ref}
                >
                    {renderInput}
                </Popover>
            </FlexRow>
        </>
    );
};
export default forwardRef(DatePicker);
